import { Button } from "@/shared/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/shared/components/ui/command";
import { Label } from "@/shared/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import type { AuthContextInterface } from "@/shared/lib/authorization/auth-context";
import { AuthContext } from "@/shared/lib/authorization/auth-context";
import { Menu } from "@headlessui/react";
import { Check, ChevronsUpDown } from "lucide-react";
import type { PropsWithChildren } from "react";
import { useMemo, useState } from "react";
import { v4 } from "uuid";
import { cn } from "../utils";
import { Role, RoleList } from "./roles";
import type { User } from "./user";

const RoleAdmin = "Admin";
const RoleDirector = "Director";
const RoleBranchManager = "BranchManager";

/**
 * DevAuthContextProvider sets a default user in the AuthContext and
 * allows the developer to modify the role of the user for easier development.
 *
 * @param props
 * @constructor
 */
export function DevAuthContextProvider(props: PropsWithChildren) {
  const { children } = props;
  const [user, setUser] = useState<User | null>({
    id: v4(),
    fullname: "Dev User",
    roles: [RoleAdmin],
    groups: [],
    initials: "DU",
    email: "test@dev.de",
  });

  const devAuthContext: AuthContextInterface = useMemo(
    () => ({
      user,
      login: (u: User) => setUser(u),
      logout: () => null,
    }),
    [user],
  );

  return (
    <AuthContext.Provider value={devAuthContext}>
      <div>{children}</div>
      <div className="absolute bottom-6 left-6">
        <DevRolePicker
          role={user?.roles[0] ?? ""}
          setRole={(role) =>
            setUser({
              id: user?.id ?? "",
              fullname: user?.fullname ?? "",
              roles: [role],
              groups: user?.groups ?? [],
              initials: user?.initials ?? "",
              email: user?.email ?? "",
            })
          }
        />
      </div>
    </AuthContext.Provider>
  );
}

function DevRolePicker(props: {
  role: string;
  setRole: (role: string) => void;
}) {
  const { role, setRole } = props;
  return (
    <Menu>
      <Menu.Button className="z-30 rounded-lg border bg-white px-3 py-2 text-sm shadow-sm">
        Rolle: {role}
      </Menu.Button>
      <Menu.Items className="flex flex-col divide-y border bg-white py-2">
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              className={`${active && "bg-blue-500"}`}
              onClick={() => setRole(RoleAdmin)}
            >
              Admin
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              className={`${active && "bg-blue-500"}`}
              onClick={() => setRole(RoleDirector)}
            >
              Geschäftsführer
            </button>
          )}
        </Menu.Item>
        <Menu.Item>
          {({ active }) => (
            <button
              type="button"
              className={`${active && "bg-blue-500"}`}
              onClick={() => setRole(RoleBranchManager)}
            >
              Niederlassungsleiter
            </button>
          )}
        </Menu.Item>
      </Menu.Items>
    </Menu>
  );
}

export function RoleSelect({
  roles,
  onRolesChange: setRoles,
  hideLabel,
}: {
  roles: Role[];
  onRolesChange: (roles: Role[]) => void;
  hideLabel?: boolean;
}) {
  console.log(roles);
  const [open, setOpen] = useState(false);
  return (
    <div className="grid gap-1.5">
      {!hideLabel && <Label>Roles</Label>}
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="justify-between"
          >
            {roles.length > 0 ? roles.join(", ") : "Select Roles..."}
            <ChevronsUpDown className="opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="p-0">
          <Command>
            <CommandInput placeholder="Search Roles..." className="h-9" />
            <CommandEmpty>No role found.</CommandEmpty>
            <CommandGroup>
              {RoleList.map((role) => (
                <CommandItem
                  key={role}
                  value={role}
                  onSelect={() => {
                    if (roles.includes(role)) {
                      setRoles(roles.filter((r) => r !== role));
                    } else {
                      setRoles([...roles, role]);
                    }
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      roles.includes(role) ? "opacity-100" : "opacity-0",
                    )}
                  />
                  {role}
                </CommandItem>
              ))}
            </CommandGroup>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
